// =============================================================================
// FILE: packages/renderer/src/App.tsx
// PURPOSE: Routes wrapped with React.lazy() so the AI page, Three.js rack
//          viewer, and other heavy modules are loaded on demand. Reduces
//          initial bundle by ~70%.
// =============================================================================

import { useState, useEffect, useMemo, lazy, Suspense, useCallback, useRef } from 'react';
import { playClick, playNav, restoreSound } from './lib/soundManager.js';
import { WarpDrive } from './components/fx/WarpDrive.js';
import { R2LiveWallpaper } from './components/fx/R2LiveWallpaper.js';
import { R2MeasurementOverlay } from './components/measurement/R2MeasurementOverlay.js';
import { R2FloatingMeasurePalette } from './components/measurement/R2FloatingMeasurePalette.js';
import { WSIEGlobalLayer } from './components/wsie/WSIEGlobalLayer.js';
import { BottomStatusBar } from './components/branding/BottomStatusBar.js';
import { R2ConnectivityBadge } from './components/branding/R2ConnectivityBadge.js';
import { AppShell } from './components/layout/AppShell.js';
import { StealthCloak } from './components/stealth/StealthCloak.js';import { Sidebar } from './components/layout/Sidebar.js';
import { TopBar } from './components/layout/TopBar.js';
import { Menu as MenuIcon, X as CloseIcon } from 'lucide-react';
import { ErrorBoundary } from './components/ErrorBoundary.js';
// CommandLine removed — replaced by Emmy chatbot UI
import { SystemHealthBanner } from './components/SystemHealthBanner.js';
import { WhatYoureMissing } from './components/upsell/WhatYoureMissing.js';
import { R2ChatPanel } from './components/ai/R2ChatPanel.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { RackDrafter } from './components/public/RackDrafter.js';
import { useAuthStore } from './stores/auth.store.js';
import { getLastPersistentProject, touchPersistentProject } from './lib/localProjectPersistence.js';
import { projectContextStore } from './stores/projectContext.js';
import { installSettingsAutosave, saveSettingsSnapshot } from './lib/settingsPersistenceEngine.js';
import { initUserPrefs, resetUserPrefsScope, useWorkspacePrefs } from './hooks/useWorkspacePrefs.js';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts.js';
import { call } from './lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { useThemeStore, applyThemeVars } from './stores/theme.store.js';
import { WindowManagerProvider } from './components/windowing/WindowManager.js';
import { WorkspaceShellHost } from './workspace-shells/WorkspaceShellHost.js';
import { useFloatingWindowStore } from './stores/floatingWindow.store.js';
import { installProjectMemoryAutosave, projectMemoryStore, readLastProjectMemory } from './stores/projectMemory.store.js';

// Lazy-loaded pages — split per route. Three.js + AI live in their own chunks.
const AssetsPage          = lazy(() => import('./pages/AssetsPage.js').then((m) => ({ default: m.AssetsPage })));
const BuildingsPage       = lazy(() => import('./pages/BuildingsPage.js').then((m) => ({ default: m.BuildingsPage })));
const RacksPage           = lazy(() => import('./pages/RacksPage.js').then((m) => ({ default: m.RacksPage })));
const ProjectsPage        = lazy(() => import('./pages/ProjectsPage.js').then((m) => ({ default: m.ProjectsPage })));
const ClashDetectionPage  = lazy(() => import('./pages/ClashDetectionPage.js').then((m) => ({ default: m.ClashDetectionPage })));
const DocumentsPage       = lazy(() => import('./pages/DocumentsPage.js').then((m) => ({ default: m.DocumentsPage })));
const SyncPage            = lazy(() => import('./pages/SyncPage.js').then((m) => ({ default: m.SyncPage })));
const SettingsPage        = lazy(() => import('./pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage })));
const AIAssistantPage     = lazy(() => import('./pages/AIAssistantPage.js').then((m) => ({ default: m.AIAssistantPage })));
const CablingPage         = lazy(() => import('./pages/CablingPage.js').then((m) => ({ default: m.CablingPage })));
const CoachValidationPage = lazy(() => import('./pages/CoachValidationPage.js').then((m) => ({ default: m.CoachValidationPage })));
const CommsPage           = lazy(() => import('./pages/CommsPage.js').then((m) => ({ default: m.CommsPage })));
const AutopilotPage       = lazy(() => import('./pages/AutopilotPage.js').then((m) => ({ default: m.AutopilotPage })));
const AgentProjectsPage   = lazy(() => import('./pages/AgentProjectsPage.js').then((m) => ({ default: m.AgentProjectsPage })));
const CostTemplatesPage   = lazy(() => import('./pages/CostTemplatesPage.js').then((m) => ({ default: m.CostTemplatesPage })));
const BackupPage          = lazy(() => import('./pages/BackupPage.js').then((m) => ({ default: m.BackupPage })));
const HealthPage          = lazy(() => import('./pages/HealthPage.js').then((m) => ({ default: m.HealthPage })));
const FieldPage           = lazy(() => import('./pages/FieldPage.js'));
const PropagationPage     = lazy(() => import('./pages/PropagationPage.js'));
const DocumentControlPanel = lazy(() => import('./pages/DocumentControlPanel.js').then((m) => ({ default: m.DocumentControlPanel })));
const CreatorPage          = lazy(() => import('./pages/CreatorPage.js').then((m) => ({ default: m.CreatorPage })));
const SchedulePage         = lazy(() => import('./pages/SchedulePage.js').then((m) => ({ default: m.SchedulePage })));
const SmartIngestionPage   = lazy(() => import('./pages/SmartIngestionPage.js').then((m) => ({ default: m.SmartIngestionPage })));
const AutoReportPage       = lazy(() => import('./pages/AutoReportPage.js').then((m) => ({ default: m.AutoReportPage })));
const PricingPage          = lazy(() => import('./pages/PricingPage.js').then((m) => ({ default: m.PricingPage })));
const GlobalCommandPage    = lazy(() => import('./pages/GlobalCommandPage.js'));
const SupportPage          = lazy(() => import('./pages/SupportPage.js'));
const PartsLibraryPage     = lazy(() => import('./pages/PartsLibraryPage.js').then((m) => ({ default: m.PartsLibraryPage })));
const MonitorDashboardPage = lazy(() => import('./pages/MonitorDashboardPage.js').then((m) => ({ default: m.MonitorDashboardPage })));
const DigitalTwinPage      = lazy(() => import('./pages/DigitalTwinPage.js').then((m) => ({ default: m.DigitalTwinPage })));
const MarkupPage           = lazy(() => import('./pages/MarkupPage.js').then((m) => ({ default: m.MarkupPage })));
const CriticalInfraPage   = lazy(() => import('./pages/CriticalInfraPage.js').then((m) => ({ default: m.CriticalInfraPage })));
const IntelligencePage    = lazy(() => import('./pages/IntelligencePage.js').then((m) => ({ default: m.IntelligencePage })));
const EstimatorPage       = lazy(() => import('./pages/EstimatorPage.js').then((m) => ({ default: m.EstimatorPage })));
const CivilEstimatorPage  = lazy(() => import('./pages/CivilEstimatorPage.js').then((m) => ({ default: m.CivilEstimatorPage })));
const LaborTimelinePage   = lazy(() => import('./pages/LaborTimelinePage.js').then((m) => ({ default: m.LaborTimelinePage })));
const CertificationPassPage = lazy(() => import('./pages/CertificationPassPage.js').then((m) => ({ default: m.CertificationPassPage })));
const CapabilitiesPage    = lazy(() => import('./pages/CapabilitiesPage.js').then((m) => ({ default: m.CapabilitiesPage })));
const R2BrainPage         = lazy(() => import('./pages/R2BrainPage.js').then((m) => ({ default: m.R2BrainPage })));
const R2AcademyPage       = lazy(() => import('./pages/R2AcademyPage.js').then((m) => ({ default: m.R2AcademyPage })));
const R2NewsPage          = lazy(() => import('./pages/R2NewsPage.js').then((m) => ({ default: m.R2NewsPage })));
const R2VoicePage         = lazy(() => import('./pages/R2VoicePage.js').then((m) => ({ default: m.R2VoicePage })));
const MIEPage             = lazy(() => import('./pages/MIEPage.js'));
const R2RailEnginePage    = lazy(() => import('./pages/R2RailEnginePage.js').then((m) => ({ default: m.R2RailEnginePage })));
const FounderApprovalsPage = lazy(() => import('./pages/FounderApprovalsPage.js').then((m) => ({ default: m.FounderApprovalsPage })));
const QuoteGeneratorPage   = lazy(() => import('./pages/QuoteGeneratorPage.js').then((m) => ({ default: m.QuoteGeneratorPage })));
const R2MarineCablePage    = lazy(() => import('./pages/R2MarineCablePage.js').then((m) => ({ default: m.R2MarineCablePage })));
const R2CommunityBuilderPage = lazy(() => import('./pages/R2CommunityBuilderPage.js').then((m) => ({ default: m.R2CommunityBuilderPage })));
const R2BunkerBuilderPage  = lazy(() => import('./pages/R2BunkerBuilderPage.js').then((m) => ({ default: m.R2BunkerBuilderPage })));
const R2VenueStudioPage    = lazy(() => import('./pages/R2VenueStudioPage.js').then((m) => ({ default: m.R2VenueStudioPage })));
const R2SiteAuditPage      = lazy(() => import('./pages/R2SiteAuditPage.js').then((m) => ({ default: m.R2SiteAuditPage })));
const R2MarkupCheckoutPage = lazy(() => import('./pages/R2MarkupCheckoutPage.js').then((m) => ({ default: m.R2MarkupCheckoutPage })));
const EngineeringVendingMachinePage = lazy(() => import('./pages/EngineeringVendingMachinePage.js').then((m) => ({ default: m.EngineeringVendingMachinePage })));
const R2ManufacturingPage  = lazy(() => import('./pages/R2ManufacturingPage.js').then((m) => ({ default: m.R2ManufacturingPage })));
const R2FrequencyLabPage   = lazy(() => import('./pages/R2FrequencyLabPage.js').then((m) => ({ default: m.R2FrequencyLabPage })));
const R2IntegrationBridgePage = lazy(() => import('./pages/R2IntegrationBridgePage.js').then((m) => ({ default: m.R2IntegrationBridgePage })));
const R2HospitalGeneratorPage = lazy(() => import('./pages/R2HospitalGeneratorPage.js').then((m) => ({ default: m.R2HospitalGeneratorPage })));
const R2VerticalInfraPage  = lazy(() => import('./pages/R2VerticalInfraPage.js').then((m) => ({ default: m.R2VerticalInfraPage })));
const R2RealityTranslatorPage = lazy(() => import('./pages/R2RealityTranslatorPage.js').then((m) => ({ default: m.R2RealityTranslatorPage })));
const R2EquipmentLibraryPage = lazy(() => import('./pages/R2EquipmentLibraryPage.js').then((m) => ({ default: m.R2EquipmentLibraryPage })));
const R2CertificationPage = lazy(() => import('./pages/R2CertificationPage.js').then((m) => ({ default: m.R2CertificationPage })));
const R2FinancePage        = lazy(() => import('./pages/R2FinancePage.js').then((m) => ({ default: m.R2FinancePage })));
const R2FinanceProPage     = lazy(() => import('./pages/R2FinanceProPage.js').then((m) => ({ default: m.R2FinanceProPage })));
const R2UndergroundCorridorPage = lazy(() => import('./pages/R2UndergroundCorridorPage.js').then((m) => ({ default: m.R2UndergroundCorridorPage })));
const R2CarbonIntelligencePage = lazy(() => import('./pages/R2CarbonIntelligencePage.js').then((m) => ({ default: m.R2CarbonIntelligencePage })));
const R2BusinessCreatorPage = lazy(() => import('./pages/R2BusinessCreatorPage.js').then((m) => ({ default: m.R2BusinessCreatorPage })));
const R2MarketingControlsPage = lazy(() => import('./pages/R2MarketingControlsPage.js').then((m) => ({ default: m.R2MarketingControlsPage })));
const R2DataPacketJourneyPage = lazy(() => import('./pages/R2DataPacketJourneyPage.js'));
const R2ManagedServicePage = lazy(() => import('./pages/R2ManagedServicePage.js').then((m) => ({ default: m.R2ManagedServicePage })));
const BIMProjectConfigPage = lazy(() => import('./pages/BIMProjectConfigPage.js'));
const BIMOperatorDashboard = lazy(() => import('./pages/BIMOperatorDashboard.js'));
const BIMGenerationEnginePage = lazy(() => import('./pages/BIMGenerationEnginePage.js').then(m => ({ default: m.BIMGenerationEnginePage })));
const MarketplacePage      = lazy(() => import('./pages/MarketplacePage.js'));
const MarketplaceOrdersPage = lazy(() => import('./pages/MarketplaceOrdersPage.js').then(m => ({ default: m.MarketplaceOrdersPage })));
const MarketplaceOperatorPage = lazy(() => import('./pages/MarketplaceOperatorPage.js'));
const InvestorRelationsPage    = lazy(() => import('./pages/InvestorRelationsPage.js').then(m => ({ default: m.InvestorRelationsPage })));
const RCDIndexPage             = lazy(() => import('./pages/RCDIndexPage.js').then(m => ({ default: m.RCDIndexPage })));
const JobQueuePage             = lazy(() => import('./pages/JobQueuePage.js'));
const FeatureFlagManagerPage   = lazy(() => import('./pages/FeatureFlagManagerPage.js'));
const StandaloneMapPage        = lazy(() => import('./pages/StandaloneMapPage.js').then(m => ({ default: m.StandaloneMapPage })));
const CIEPage                  = lazy(() => import('./pages/CIEPage.js').then(m => ({ default: m.CIEPage })));
const AutomationStudioPage     = lazy(() => import('./pages/AutomationStudioPage.js').then(m => ({ default: m.AutomationStudioPage })));
const R2CalendarPage           = lazy(() => import('./pages/R2CalendarPage.js').then(m => ({ default: m.R2CalendarPage })));
const R2SafetyPage             = lazy(() => import('./pages/R2SafetyPage.js').then(m => ({ default: m.R2SafetyPage })));
const R2FinancialCommandPage   = lazy(() => import('./pages/R2FinancialCommandPage.js').then(m => ({ default: m.R2FinancialCommandPage })));
const R2TimesheetPage          = lazy(() => import('./pages/R2TimesheetPage.js').then(m => ({ default: m.R2TimesheetPage })));
const R2PIEPage                = lazy(() => import('./pages/R2PIEPage.js').then(m => ({ default: m.R2PIEPage })));
const R2FinanceHubPage         = lazy(() => import('./pages/R2FinanceHubPage.js').then(m => ({ default: m.R2FinanceHubPage })));
const R2MissionControlPage     = lazy(() => import('./pages/R2MissionControlPage.js').then(m => ({ default: m.R2MissionControlPage })));
const R2AICoachPage            = lazy(() => import('./pages/R2AICoachPage.js').then(m => ({ default: m.R2AICoachPage })));
const R2PermitIntelligencePage = lazy(() => import('./pages/R2PermitIntelligencePage.js').then(m => ({ default: m.R2PermitIntelligencePage })));
const R2CoreMapPage            = lazy(() => import('./pages/R2CoreMapPage.js').then(m => ({ default: m.R2CoreMapPage })));
const R2StudioPage             = lazy(() => import('./pages/R2StudioPage.js'));
const R2CharacterCreatorPage   = lazy(() => import('./pages/R2CharacterCreatorPage.js'));
const R2ProductionStudioPage   = lazy(() => import('./pages/R2ProductionStudioPage.js'));
const R2CalendarEnginePage     = lazy(() => import('./pages/R2CalendarEnginePage.js'));
const R2VoiceEnginePage        = lazy(() => import('./pages/R2VoiceEnginePage.js'));
const R2RoleAgentPage          = lazy(() => import('./pages/R2RoleAgentPage.js'));
const R2CinematicScenePage     = lazy(() => import('./pages/R2CinematicScenePage.js'));
const R2OldEarthWorldPage      = lazy(() => import('./pages/R2OldEarthWorldPage.js').then((m) => ({ default: m.R2OldEarthWorldPage })));
const R2CSIEPage               = lazy(() => import('./pages/R2CSIEPage.js'));
const R2SCSEPage               = lazy(() => import('./pages/R2SCSEPage.js'));
const R2ECEPage                = lazy(() => import('./pages/R2ECEPage.js'));
const R2PCEPage                = lazy(() => import('./pages/R2PCEPage.js'));
const R2VCEPage                = lazy(() => import('./pages/R2VCEPage.js'));
const R2WBEPage                = lazy(() => import('./pages/R2WBEPage.js'));
const R2AIEPage                = lazy(() => import('./pages/R2AIEPage.js'));
const R2CreativeStudioPage     = lazy(() => import('./pages/R2CreativeStudioPage.js'));
const R2GLEPage                = lazy(() => import('./pages/R2GLEPage.js'));
const R2MVIEPage               = lazy(() => import('./pages/R2MVIEPage.js'));
const R2AIQEPage               = lazy(() => import('./pages/R2AIQEPage.js'));
const R2CTEEPage               = lazy(() => import('./pages/R2CTEEPage.js'));
const R2REEPage                = lazy(() => import('./pages/R2REEPage.js'));
const R2ARIEPage               = lazy(() => import('./pages/R2ARIEPage.js'));
const R2VIEPage                = lazy(() => import('./pages/R2VIEPage.js'));
const R2FounderCommandCenterPage = lazy(() => import('./pages/R2FounderCommandCenterPage.js'));
const R2CCFEPage               = lazy(() => import('./pages/R2CCFEPage.js'));
const R2LIEPage                = lazy(() => import('./pages/R2LIEPage.js'));
const R2CATEPage               = lazy(() => import('./pages/R2CATEPage.js'));
const R2BSWEPage               = lazy(() => import('./pages/R2BSWEPage.js'));
const R2RAEPage                = lazy(() => import('./pages/R2RAEPage.js'));
const R2DesignLanguagePage     = lazy(() => import('./pages/R2DesignLanguagePage.js'));
const R2DCREPage               = lazy(() => import('./pages/R2DCREPage.js'));
const R23DIntelligencePage     = lazy(() => import('./pages/R23DIntelligencePage.js'));
const R2SmartViewerPage        = lazy(() => import('./pages/R2SmartViewerPage.js'));
const R2ISLEPage               = lazy(() => import('./pages/R2ISLEPage.js'));
const R2MPCEPage               = lazy(() => import('./pages/R2MPCEPage.js'));
const R2WIIEPage               = lazy(() => import('./pages/R2WIIEPage.js'));
const R2LVTDEPage              = lazy(() => import('./pages/R2LVTDEPage.js'));
const R2LICEPage               = lazy(() => import('./pages/R2LICEPage.js'));
const R2UIIEPage               = lazy(() => import('./pages/R2UIIEPage.js'));
const R2CTIEPage               = lazy(() => import('./pages/R2CTIEPage.js'));
const R2MFIEPage               = lazy(() => import('./pages/R2MFIEPage.js'));
const R2CTDTEPage              = lazy(() => import('./pages/R2CTDTEPage.js'));
const R2ASEPage                = lazy(() => import('./pages/R2ASEPage.js'));
const R2MeetingIntelligencePage = lazy(() => import('./pages/R2MeetingIntelligencePage.js'));
const R2PermissionsPage        = lazy(() => import('./pages/R2PermissionsPage.js'));
const R2ArchEnginePage         = lazy(() => import('./pages/R2ArchEnginePage.js'));
const R2DCProfilePage          = lazy(() => import('./pages/R2DCProfilePage.js'));
const R2AnnotationsPage        = lazy(() => import('./pages/R2AnnotationsPage.js'));
const R2WSIEPage               = lazy(() => import('./pages/R2WSIEPage.js'));
const R2MeasurementEnginePage  = lazy(() => import('./pages/R2MeasurementEnginePage.js'));
const R2IIEPage                = lazy(() => import('./pages/R2IIEPage.js'));
const R2AIGatewayPage          = lazy(() => import('./pages/R2AIGatewayPage.js'));
const R2MemoryEnginePage       = lazy(() => import('./pages/R2MemoryEnginePage.js'));
const R2GlobeEnginePage        = lazy(() => import('./pages/R2GlobeEnginePage.js'));
const R2InfraJourneyPage       = lazy(() => import('./pages/R2InfraJourneyPage.js'));
const R2DigitalTwinLibraryPage = lazy(() => import('./pages/R2DigitalTwinLibraryPage.js'));
const R2BIMStudioPage          = lazy(() => import('./pages/R2BIMStudioPage.js'));
const R2GlobalOpsCenterPage    = lazy(() => import('./pages/R2GlobalOpsCenterPage.js'));
import { InvestorPublicPage }   from './pages/InvestorPublicPage.js';
import InvestorSafeDemoPage from './pages/InvestorSafeDemoPage.js';

import { Footer } from './components/branding/Footer.js';
import { Splash } from './components/branding/Splash.js';
import R2VendingMachineLanding from './pages/R2VendingMachineLanding.js';
const R2MarketplacePage = lazy(() => import('./pages/R2MarketplacePage.js').then(m => ({ default: m.R2MarketplacePage })));
const R2DeliveryPortalPage = lazy(() => import('./pages/R2DeliveryPortalPage.js').then(m => ({ default: m.R2DeliveryPortalPage })));
const R2CustomerDashboardPage = lazy(() => import('./pages/R2CustomerDashboardPage.js').then(m => ({ default: m.R2CustomerDashboardPage })));
const R2AIDrawingGeneratorPage = lazy(() => import('./pages/R2AIDrawingGeneratorPage.js').then(m => ({ default: m.R2AIDrawingGeneratorPage })));
const R2DesignPackageGeneratorPage = lazy(() => import('./pages/R2DesignPackageGeneratorPage.js').then(m => ({ default: m.R2DesignPackageGeneratorPage })));
const R2GISIntelligencePage = lazy(() => import('./pages/R2GISIntelligencePage.js').then(m => ({ default: m.R2GISIntelligencePage })));
const R2IntelligentBuildingPage = lazy(() => import('./pages/R2IntelligentBuildingPage.js').then(m => ({ default: m.R2IntelligentBuildingPage })));
const R2BINSPage = lazy(() => import('./pages/R2BINSPage.js').then(m => ({ default: m.R2BINSPage })));
const R2EPEPage = lazy(() => import('./pages/R2EPEPage.js').then(m => ({ default: m.R2EPEPage })));
const R2SustainabilityPage = lazy(() => import('./pages/R2SustainabilityPage.js').then(m => ({ default: m.R2SustainabilityPage })));
import { OfflineBanner } from './components/layout/OfflineBanner.js';
import { DemoModeToggle } from './components/DemoMode.js';
import { GrowthCelebration } from './components/growth/GrowthCelebration.js';
import { GatekeeperAlerts } from './components/growth/GatekeeperAlerts.js';
import { AnthemPlayer } from './components/branding/AnthemPlayer.js';
import { ScreenshotButton } from './components/capture/ScreenshotButton.js';
import { LicenseGate } from './components/license/LicenseGate.js';
import { useLicense } from './hooks/useLicense.js';
import { ProjectFileExplorer } from './components/files/ProjectFileExplorer.js';
import { usePfeStore, pfeStore } from './stores/pfe.store.js';
import { QuickIngestDialog } from './components/sie/QuickIngestDialog.js';
import { ingestStore, useIngestStore } from './stores/ingest.store.js';
import { CyberpunkLauncher } from './components/launcher/CyberpunkLauncher.js';
import { CorporateIntercom } from './components/intercom/CorporateIntercom.js';
import { GlobalAvatarOverlay } from './components/avatar/GlobalAvatarOverlay.js';
import { FocusBeamExitChip }    from './components/nightOps/NightOpsPanel.js';
import { LicenseLock } from './components/auth/LicenseLock.js';
import { AnxietyControlPanel } from './components/r2/AnxietyControlPanel.js';
import { R2CaptureLauncher } from './components/capture/R2CaptureLauncher.js';
import { FloatingUndoTile } from './components/undo/FloatingUndoTile.js';
import { ConfidenceModal }  from './components/undo/ConfidenceModal.js';
import { FloatingAutomationTile } from './components/automation/FloatingAutomationTile.js';
import { SessionResumeBanner } from './components/session/SessionResumeBanner.js';
import { useSessionResume, getRouteMeta } from './stores/sessionResume.store.js';
import { HoverIntelligenceProvider } from './components/hover/HoverIntelligenceProvider.js';
import { TransitionAnnouncementModal } from './components/modals/TransitionAnnouncementModal.js';
import { CommandPalette } from './components/ux/CommandPalette.js';
import { RightClickMenu } from './components/ux/RightClickMenu.js';
import { RadialMenu } from './components/ux/RadialMenu.js';
import { NightLightProvider } from './components/ux/NightLightProvider.js';
import { useUXControlStore } from './stores/uxControl.store.js';
import { useUndoEngine } from './stores/undoEngine.store.js';
import { applyExperienceMode, useExperienceMode } from './stores/experienceMode.store.js';
import { useFounderMode } from './stores/founderMode.store.js';
import { useNightOpsStore, getTextScaleMultiplier, NIGHT_PRESETS } from './stores/nightOps.store.js';
import { getStoredLegacyKey, verifyDeviceLicense } from './lib/hardwareId.js';

export type Route =
  | 'dashboard' | 'assets' | 'buildings' | 'racks' | 'projects'
  | 'clashes' | 'documents' | 'sync' | 'ai' | 'settings' | 'cabling'
  | 'coach-validation' | 'comms' | 'autopilot' | 'agent' | 'cost'
  | 'backup' | 'health' | 'field' | 'propagation' | 'doc-control'
  | 'creator' | 'schedule' | 'sie' | 'auto-report' | 'pricing' | 'global-command' | 'help'
  | 'parts-library' | 'monitor' | 'critical-infra' | 'intelligence' | 'estimator' | 'civil' | 'labor-timeline' | 'certification-pass' | 'capabilities' | 'r2-brain' | 'r2-academy' | 'r2-news' | 'r2-voice' | 'rail-engine' | 'founder-approvals' | 'quote-generator' | 'marine-cable' | 'community-builder' | 'bunker-builder' | 'venue-studio' | 'manufacturing' | 'frequency-lab' | 'integration-bridge' | 'site-audit' | 'markup-checkout' | 'vending-machine' | 'hospital-generator' | 'viie' | 'reality-translator' | 'finance' | 'finance-pro'
 | 'underground-corridor' | 'carbon-intelligence' | 'business-creator' | 'marketing-controls' | 'managed-services'
 | 'bim-project-config' | 'bim-operator-dashboard' | 'bim-engine'
 | 'investor-relations' | 'mie' | 'job-queue'
 | 'marketplace' | 'marketplace-orders' | 'marketplace-operator'
 | 'feature-flags' | 'global-map'
 | 'voice' | 'profile' | 'r2-finance'
 | 'cie'
 | 'automation-studio'
 | 'r2-calendar'
 | 'r2-safety'
 | 'r2-financial-command'
 | 'r2-timesheet'
 | 'r2-marketplace'
 | 'r2-pie'
 | 'r2-permit-intelligence'
 | 'r2-core-map'
 | 'r2-finance-hub'
 | 'r2-mission-control'
 | 'r2-ai-coach'
 | 'r2-studio'
 | 'r2-character-creator'
 | 'r2-production-studio'
 | 'r2-calendar-engine'
 | 'r2-voice-engine'
 | 'r2-role-agent'
 | 'r2-cinematic-scene'
 | 'r2-csie'
 | 'r2-scse'
 | 'r2-ece'
 | 'r2-pce'
 | 'r2-vce'
 | 'r2-wbe'
 | 'r2-aie'
 | 'r2-creative-studio'
 | 'r2-gle'
 | 'r2-mvie'
 | 'r2-aiqe'
 | 'r2-ctee'
 | 'r2-ree'
 | 'r2-arie'
 | 'r2-vie'
 | 'r2-ccfe'
 | 'r2-lie'
 | 'r2-cate'
 | 'r2-founder-command-center'
 | 'r2-bwse'
 | 'r2-rae'
 | 'r2-r2dl'
 | 'r2-dcre'
| 'r2-3d-engine'
| 'r2-smart-viewer'
| 'r2-arch-engine'
| 'r2-dc-profile'
| 'r2-annotations'
| 'r2-isle'
| 'r2-mpce'
| 'r2-wiie'
| 'r2-lvtde'
| 'r2-lice'
| 'r2-uiie'
| 'r2-ctie'
| 'r2-mfie'
| 'r2-ctdte'
| 'r2-ase'
| 'r2-meeting-intelligence'
| 'r2-permissions'
| 'r2-wsie'
| 'r2-measurement-engine'
| 'r2-iie'
| 'r2-ai-gateway'
| 'r2-memory-engine'
| 'r2-globe'
| 'r2-infra-journey'
| 'r2-digital-twin-library'
| 'r2-bim-studio'
| 'r2-global-ops-center'
| 'r2-old-earth-world'
 | 'r2-landing'
 | 'r2-delivery-portal'
 | 'r2-customer-dashboard'
 | 'r2-vending-machine'
 | 'data-packet-journey'
 | 'rack-maker-preview'
 | 'rack-maker'
 | 'rack-maker-activate'
 | 'r2-drawing-generator'
 | 'r2-design-package-generator'
 | 'r2-gis-intelligence'
 | 'r2-intelligent-building'
 | 'r2-bins-explorer'
 | 'r2-epe'
 | 'r2-sustainability'
  | 'r2-equipment-library'
 | 'r2-certification'
 | 'index';

function PageFallback(): JSX.Element {
  return (
    <div data-testid="page-fallback" className="p-12 text-zinc-500 text-[12px] font-mono">
      Loading module…
    </div>
  );
}

export function App(): JSX.Element {
  // ── Hash-based routing — URL reflects current page so links are shareable ───
  const getHashRoute = (): Route => {
    if (typeof window === 'undefined') return 'dashboard';
    const h = window.location.hash.slice(1).split('?')[0];
    if (h && h !== 'investor' && h.length > 0) return h as Route;
    return 'dashboard';
  };

  const [route, setRoute] = useState<Route>(getHashRoute);
  const [navOpen, setNavOpen] = useState(false);
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [splashDone, setSplashDone] = useState(initialHash === '#login');
  const [showLogin, setShowLogin] = useState(initialHash === '#login');
  const [drawingFocusMode, setDrawingFocusMode] = useState(false);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const explicitNavigationRef = useRef(false);
  const sessionResume = useSessionResume();
  const { past } = useUndoEngine();
  const { user, hydrate, login } = useAuthStore();
  const { prefs } = useWorkspacePrefs();
  const { licenseStatus, verify } = useLicense();
  const founderUser = user?.role === 'Founder' || user?.roles?.includes('Founder');

  // Enhanced navigate — updates URL hash on every navigation
  const navigate = (r: Route): void => {
    explicitNavigationRef.current = true;
    // Record where we're leaving before navigating away
    const meta = getRouteMeta(route);
    const lastAction = past[past.length - 1]?.label;
    sessionResume.recordRouteVisit({
      route,
      routeLabel: meta.label,
      routeIcon: meta.icon,
      lastAction,
      lastActionCategory: past[past.length - 1]?.category,
    });
    setRoute(r);
    projectMemoryStore.getState().recordRoute(r);
    setNavOpen(false);
    if (typeof window !== 'undefined') window.history.pushState(null, '', `#${r}`);
    void saveSettingsSnapshot('navigation');
  };

  useEffect(() => {
    void getLastPersistentProject().then((project) => {
      const remembered = readLastProjectMemory();
      if (remembered?.projectId && remembered.projectId !== 'workspace') {
        projectContextStore.setActiveProject(remembered.projectId);
        const memory = projectMemoryStore.getState().hydrate(user?.email ?? undefined, remembered.projectId);
        if (memory.lastActiveRoute && (!window.location.hash || window.location.hash === '#dashboard')) {
          setRoute(memory.lastActiveRoute as Route);
          window.history.replaceState(null, '', `#${memory.lastActiveRoute}`);
        }
        setRestoreNotice(`Restored workspace memory: ${remembered.projectId}`);
        setTimeout(() => setRestoreNotice(null), 4500);
        return;
      }
      if (!project) return;
      projectContextStore.setActiveProject(project.id);
      projectMemoryStore.getState().hydrate(user?.email ?? undefined, project.id);
      void touchPersistentProject(project.id);
      setRestoreNotice(`Restored project: ${project.name}`);
      setTimeout(() => setRestoreNotice(null), 4500);
      if (!window.location.hash || window.location.hash === '#dashboard') {
        setRoute('projects');
        window.history.replaceState(null, '', '#projects');
      }
    });
  }, [user?.email]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#login') setShowLogin(true);
  }, []);

  useEffect(() => {
    if (user && typeof window !== 'undefined' && window.location.hash === '#login') {
      setRoute('dashboard');
      window.history.replaceState(null, '', '#dashboard');
    }
  }, [user]);

  // Browser back / forward support
  useEffect(() => {
    const onPop = () => setRoute(getHashRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => installProjectMemoryAutosave(), []);

  useEffect(() => {
    projectMemoryStore.getState().recordRoute(route);
  }, [route]);

  // Global navigation event — allows any component to navigate via:
  //   window.dispatchEvent(new CustomEvent('r2-navigate', { detail: { route: 'r2-academy' } }))
  useEffect(() => {
    const onNav = (e: Event) => {
      const r = (e as CustomEvent<{ route: string }>).detail?.route;
      if (r) navigate(r as Route);
    };
    window.addEventListener('r2-navigate', onNav);
    return () => window.removeEventListener('r2-navigate', onNav);
  }, [route]);
  const [hwLock, setHwLock] = useState<{ locked: boolean; message: string } | null>(null);

  // ── Hardware-Locked Licensing — launch-time verification (Legacy tier) ─────
  // Inert unless a Legacy license key was stored on this device.
  useEffect(() => {
    const key = getStoredLegacyKey();
    if (!key) return;
    void (async () => {
      try {
        const v = await verifyDeviceLicense(key);
        if (v.locked) setHwLock({ locked: true, message: v.message ?? '' });
      } catch { /* offline — fail open */ }
    })();
  }, []);

  // ── Sound system bootstrap ─────────────────────────────────────────────
  useEffect(() => {
    restoreSound();

    // Global click sound: fires on ANY click across BIM OPS. Interactive
    // elements opt out with [data-nosound] when they play their own sound.
    const handleClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el.closest('[data-nosound]')) return;                       // element plays its own sound
      if (el.closest('button[disabled], [aria-disabled="true"]')) return;
      const isNav = !!el.closest('[data-testid="sidebar"]');
      if (isNav) playNav(); else playClick();
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, []);
  useEffect(() => {
    if (user?.email) initUserPrefs(user.email);
    else resetUserPrefsScope();
  }, [user?.email]);

  // ── Display Experience Engine — apply theme on mount + when it changes ───────
  // Use targeted selector to avoid App re-rendering on every theme store update.
  const activeTheme = useThemeStore((s) => s.activeTheme);
  useEffect(() => {
    // Access autoDetectTheme via getState() to avoid unstable reference in deps.
    const resolved = useThemeStore.getState().autoDetectTheme();
    applyThemeVars(resolved);
  }, [activeTheme]);

  // Wraps verify so PayPal PP-XXXXXX keys auto-pass without needing a BICSI format check
  const verifyLicenseKey = useCallback(async (key: string): Promise<{ ok: boolean; error?: string }> => {
    if (key.startsWith('PP-')) {
      // PayPal-activated license — payment captured by backend, just accept it
      try {
        await call<string, { ok: boolean }>(IPC.LICENSE_VERIFY, key);
      } catch { /* web preview — no main process, ignore */ }
      return { ok: true };
    }
    return verify(key);
  }, [verify]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    void saveSettingsSnapshot('workspace-prefs-change');
  }, [prefs]);

  // Night Operations Mode — sync store to html data-attributes so CSS takes over globally
  const nightOps = useNightOpsStore();
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.nightOps = String(nightOps.active);
    if (nightOps.active) {
      const p = NIGHT_PRESETS[nightOps.preset];
      root.dataset.nightPreset = nightOps.preset;
      root.dataset.focusBeam    = String(nightOps.focusBeam);
      root.dataset.reduceGlow   = String(nightOps.reduceGlow);
      root.dataset.reduceMotion = String(nightOps.reduceMotion);
      // Brightness + contrast + text scale via CSS custom properties
      const scale = getTextScaleMultiplier(nightOps.textScale);
      root.style.setProperty('--night-brightness-val', String(nightOps.brightness / 100));
      root.style.setProperty('--night-contrast-val',   String(nightOps.contrast / 100));
      root.style.setProperty('--night-text-scale-val', String(scale));
      // Token overrides
      root.style.setProperty('--night-bg',     p.tokens.bg);
      root.style.setProperty('--night-panel',  p.tokens.panel);
      root.style.setProperty('--night-border', p.tokens.border);
      root.style.setProperty('--night-fg',     p.tokens.fg);
      root.style.setProperty('--night-muted',  p.tokens.muted);
      root.style.setProperty('--night-accent', p.tokens.accent);
    } else {
      // Clear all night overrides so normal theme is restored
      ['--night-bg','--night-panel','--night-border','--night-fg','--night-muted','--night-accent',
       '--night-brightness-val','--night-contrast-val','--night-text-scale-val'].forEach((k) => {
        root.style.removeProperty(k);
      });
      delete root.dataset.nightPreset;
      delete root.dataset.focusBeam;
      delete root.dataset.reduceGlow;
      delete root.dataset.reduceMotion;
    }
  }, [
    nightOps.active, nightOps.preset, nightOps.brightness, nightOps.contrast,
    nightOps.textScale, nightOps.focusBeam, nightOps.reduceGlow, nightOps.reduceMotion,
  ]);

  // Night Ops voice command listener
  useEffect(() => {
    const nightCmds = [
      'activate night mode', 'night mode', 'flashlight mode', 'flashlight on',
      'working in the dark', "i'm working in the dark", 'dark mode',
    ];
    const presetCmds: Record<string, string> = {
      'cinema mode':            'cinema',
      'blueprint mode':         'blueprint',
      'field inspection mode':  'field-inspection',
      'dark office mode':       'dark-office',
    };
    const handler = (e: Event) => {
      const text = ((e as CustomEvent).detail?.text ?? '').toLowerCase();
      if (!text) return;
      const nightOpsStore = useNightOpsStore.getState();
      // Check for deactivate commands
      if (text.includes('deactivate night') || text.includes('night mode off') || text.includes('exit night')) {
        nightOpsStore.deactivate();
        return;
      }
      // Check for preset commands
      for (const [cmd, preset] of Object.entries(presetCmds)) {
        if (text.includes(cmd)) {
          nightOpsStore.setPreset(preset as Parameters<typeof nightOpsStore.setPreset>[0]);
          nightOpsStore.activate();
          return;
        }
      }
      // Generic night commands
      if (nightCmds.some((cmd) => text.includes(cmd))) {
        nightOpsStore.activate();
      }
    };
    window.addEventListener('r2:voice:command', handler);
    return () => window.removeEventListener('r2:voice:command', handler);
  }, []);

  // Theme switch — opt-in Neon Cyberpunk Mode
  useEffect(() => {
    if (prefs.theme === 'neon-cyberpunk') {
      document.documentElement.setAttribute('data-theme', 'neon-cyberpunk');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [prefs.theme]);

  // R2 Core Map keyboard shortcut — Ctrl+Shift+F
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault();
        setRoute((r) => r === 'r2-core-map' ? 'dashboard' : 'r2-core-map');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // R2 Universal Search keyboard shortcut — Ctrl+K / Shift+Shift
  useEffect(() => {
    let lastShift = 0;
    const onKey = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        useUXControlStore.getState().openCommandPalette();
        return;
      }
      // Also Ctrl+Shift+Space
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'Space') {
        e.preventDefault();
        useUXControlStore.getState().openCommandPalette();
        return;
      }
      // Double-Shift (Shift pressed twice within 400ms)
      if (e.key === 'Shift' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const now = Date.now();
        if (now - lastShift < 400) {
          e.preventDefault();
          useUXControlStore.getState().openCommandPalette();
          lastShift = 0;
        } else {
          lastShift = now;
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // UI text mode — CSS-only uppercase transform applied via data-ui-text on
  // <html>. Default `normal` removes the attribute and the app renders as-is.
  useEffect(() => {
    if (prefs.uiTextMode === 'uppercase') {
      document.documentElement.setAttribute('data-ui-text', 'uppercase');
    } else {
      document.documentElement.removeAttribute('data-ui-text');
    }
  }, [prefs.uiTextMode]);

  // Background theme — sets `data-bg-theme` on <html> (palettes live in
  // styles/background-themes.css). When the user picks "custom", expose the
  // chosen hex as an rgb triple in --bg-custom so the CSS rule can apply it.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-bg-theme', prefs.backgroundTheme);
    if (prefs.backgroundTheme === 'custom') {
      const hex = prefs.customBackgroundColor.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      root.style.setProperty('--bg-custom', `${r} ${g} ${b}`);
    } else {
      root.style.removeProperty('--bg-custom');
    }
  }, [prefs.backgroundTheme, prefs.customBackgroundColor]);

  // R2 Adaptive Experience — re-apply the persisted Dim / Anxiety Control mode
  // on boot so the calm environment survives reloads.
  useEffect(() => {
    applyExperienceMode(useExperienceMode.getState().mode);
    void useFounderMode.getState().hydrate();
    return installSettingsAutosave();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      setDrawingFocusMode(Boolean((e as CustomEvent<{ active?: boolean }>).detail?.active));
    };
    window.addEventListener('r2:drawing-focus-mode', handler);
    return () => window.removeEventListener('r2:drawing-focus-mode', handler);
  }, []);

  // Minimal Portable Mode — forces the user back to Dashboard / Settings if
  // they were on a route that gets hidden by the sidebar. Keeps the routing
  // surface intact so disabling minimal mode restores prior navigation.
  useEffect(() => {
    if (prefs.minimalMode && route !== 'dashboard' && route !== 'settings' && route !== 'creator') {
      navigate('dashboard');
    }
  }, [prefs.minimalMode, route]);

  // Optional auto-restore — disabled by default. Only fires when the user
  // has explicitly opted in via Settings AND a lastProjectId exists.
  // Runs once after login to keep the portable shell deterministic.
  const [autoRestoreApplied, setAutoRestoreApplied] = useState(false);
  useEffect(() => {
    if (!user || autoRestoreApplied) return;
    // Record login so R2 can show the "where you left off" banner
    sessionResume.recordLogin();
    if (prefs.openLastProject && prefs.lastProjectId) {
      navigate('projects');
    }
    setAutoRestoreApplied(true);
  }, [user, prefs.openLastProject, prefs.lastProjectId, autoRestoreApplied]);

  // Founder users land directly on the Founder Workspace / Global Command Centre.
  const [didInitialRoute, setDidInitialRoute] = useState(false);
  useEffect(() => {
    if (!user || didInitialRoute) return;
    if (user.role === 'Founder' || user.roles?.includes('Founder')) {
      const currentHash = typeof window !== 'undefined' ? window.location.hash.slice(1).split('?')[0] : '';
      if (!explicitNavigationRef.current && (!currentHash || currentHash === 'dashboard') && !projectMemoryStore.getState().snapshot.lastActiveRoute) {
        navigate('global-command');
      }
    }
    setDidInitialRoute(true);
  }, [user, didInitialRoute]);

  // Desktop UX — sync the persisted `alwaysOnTop` preference to the Electron
  // main process exactly once per session (and again whenever the user
  // toggles it from Settings). The Settings card also calls this IPC
  // immediately on change; doing it here too guarantees the window respects
  // the saved preference after a cold start.
  useEffect(() => {
    void call<{ value: boolean }, { ok: boolean; value: boolean }>(
      IPC.SYSTEM_SET_ALWAYS_ON_TOP,
      { value: prefs.alwaysOnTop },
    ).catch(() => { /* dev / web preview has no main process */ });
  }, [prefs.alwaysOnTop]);

  // Desktop UX — global keyboard shortcuts. Handlers are memoised so the
  // effect inside `useKeyboardShortcuts` doesn't churn on every render.
  const shortcutHandlers = useMemo(() => ({
    onProjects: () => setRoute('projects'),
    onSettings: () => setRoute('settings'),
    onSearch:   () => {
      const el = document.querySelector<HTMLInputElement>('[data-testid="global-search"]');
      el?.focus();
      el?.select();
    },
  }), []);
  useKeyboardShortcuts(shortcutHandlers);

  // ── Public investor transparency page (no login required) ─────────────────
  const investorHash = typeof window !== 'undefined' ? window.location.hash : '';
  if (investorHash === '#rack-maker-preview') {
    return (
      <>
        <R2ConnectivityBadge />
        <RackDrafter onClose={() => { window.history.replaceState(null, '', '#r2-vending-machine'); setRoute('r2-vending-machine'); }} />
      </>
    );
  }
  if (investorHash === '#data-packet-journey') {
    return <><R2ConnectivityBadge /><Suspense fallback={<div style={{ color: '#fff', padding: 24 }}>Loading packet journey…</div>}><R2DataPacketJourneyPage /></Suspense></>;
  }
  if (investorHash === '#r2-design-package-generator') {
    return <><R2ConnectivityBadge /><Suspense fallback={<PageFallback />}><R2DesignPackageGeneratorPage /></Suspense></>;
  }
  if (investorHash === '#rack-maker-activate' && !user) {
    return (
      <>
        <LoginPage />
        <R2ConnectivityBadge />
        {hwLock && <LicenseLock message={hwLock.message} />}
      </>
    );
  }
  if (investorHash === '#investor-demo' || investorHash === '#demo') {
    return <><R2ConnectivityBadge /><InvestorSafeDemoPage /></>;
  }
  const isPublicInvestorPage = investorHash === '#investor' || investorHash.startsWith('#investor-');
  const investorToken = investorHash.startsWith('#investor-') ? investorHash.slice('#investor-'.length) : null;
  if (isPublicInvestorPage) {
    return <><R2ConnectivityBadge /><InvestorPublicPage token={investorToken} onLogin={() => { window.location.hash = ''; window.location.reload(); }} /></>;
  }

  if (investorHash === '#login' && !user) {
    return (
      <>
        <LoginPage />
        <R2ConnectivityBadge />
        {hwLock && <LicenseLock message={hwLock.message} />}
      </>
    );
  }

  if (!user) {
    if (!splashDone) {
      return (
        <>
          <Splash onDone={() => setSplashDone(true)} />
          <R2ConnectivityBadge />
          {hwLock && <LicenseLock message={hwLock.message} />}
        </>
      );
    }
    if (!showLogin) {
      return (
        <>
          <R2VendingMachineLanding onSignIn={() => { setShowLogin(true); window.history.replaceState(null, '', '#login'); }} />
          <R2ConnectivityBadge />
          {false && <TransitionAnnouncementModal />}
        </>
      );
    }
    return (
      <>
        <LoginPage />
        <R2ConnectivityBadge />
        {hwLock && <LicenseLock message={hwLock.message} />}
      </>
    );
  }

  return (
    <>
      {/* R2 Smart Hover Intelligence — global contextual palette */}
      <R2ConnectivityBadge />
      <HoverIntelligenceProvider />
      {/* R2 Night Light Navigation — CSS variable injector */}
      <NightLightProvider />
      {hwLock && <LicenseLock message={hwLock.message} />}
      {!nightOps.active && <WarpDrive backgroundEffectsEnabled={prefs.backgroundEffectsEnabled} />}
      {!drawingFocusMode && <R2LiveWallpaper />}
      {!drawingFocusMode && <R2MeasurementOverlay />}
      {!drawingFocusMode && <R2FloatingMeasurePalette />}
      {!drawingFocusMode && <WSIEGlobalLayer />}
      {/* Offline Mode Banner — shows when user loses connectivity */}
      <OfflineBanner />
      {/* Stealth Workspace — full-screen privacy cloak (renders when active) */}
      <StealthCloak userEmail={user?.email} />
      {/* Global Emmy Growth Agent celebration toast */}
      <GrowthCelebration />
      {/* Founder's Gatekeeper — real-time blocked-attempt alerts (master admin) */}
      <GatekeeperAlerts />
      {/* R2 Anxiety Control — protective calm companion (renders only in Anxiety mode) */}
      <AnxietyControlPanel />
      {/* R2 Capture Mode — floating intelligent collection launcher */}
      {!drawingFocusMode && <R2CaptureLauncher />}
      {!drawingFocusMode && <FloatingUndoTile />}
      <ConfidenceModal />
      {restoreNotice && !drawingFocusMode && (
        <div
          data-testid="project-restore-notice"
          className="fixed top-4 right-4 z-[9900] px-4 py-3 rounded-sm text-[12px] text-emerald-200 shadow-xl"
          style={{ background: 'rgba(6,20,18,0.94)', border: '1px solid rgba(94,234,212,0.35)' }}
        >
          {restoreNotice}
        </div>
      )}
      {!drawingFocusMode && <FloatingAutomationTile onNavigate={(r: string) => navigate(r as Route)} />}
      {user && !drawingFocusMode && <SessionResumeBanner onNavigate={(r: string) => navigate(r as Route)} />}
      {/* R2 UX Control Engine — Command Palette, Right-Click Menu, Radial Menu */}
      <CommandPalette onNavigate={(r: string) => navigate(r as Route)} onAction={(a) => { if (a === 'toggle-night-ops') useNightOpsStore.getState().toggle(); }} />
      <RightClickMenu onNavigate={(r: string) => navigate(r as Route)} onAction={(a) => { if (a === 'toggle-night-ops') useNightOpsStore.getState().toggle(); }} openCommandPalette={() => useUXControlStore.getState().openCommandPalette()} />
      <RadialMenu onNavigate={(r: string) => navigate(r as Route)} onAction={(a) => { if (a === 'toggle-night-ops') useNightOpsStore.getState().toggle(); }} openCommandPalette={() => useUXControlStore.getState().openCommandPalette()} />
      {/* RCD² Engine Anthem — plays once on entering the dashboard */}
      <AnthemPlayer />
      {/* Persistent viral-marketing screenshot engine (branded + QR) */}
      <ScreenshotButton moduleLabel={route} />
      {/* R2 Persistent AI Chat Panel — available across all routes */}
      {!drawingFocusMode && <R2ChatPanel onNavigate={(r: string) => navigate(r as Route)} currentRoute={route ?? 'dashboard'} />}
      {/* RCD² VERIFIED Bottom Status Bar — always visible */}
      {!drawingFocusMode && <BottomStatusBar moduleName={route ? `R2 · ${route.toUpperCase().replace(/-/g, ' ')}` : undefined} />}
      {/* License gate: renders banner during trial, full overlay after expiry */}
      {licenseStatus && !founderUser && <LicenseGate status={licenseStatus} onVerify={verify} onNavigate={(r) => navigate(r as Route)} />}
      {/* R2 Demo Mode Toggle — Developer Preview 1 */}
      {user && !drawingFocusMode && <DemoModeToggle />}
      <AppShell>
        <WorkspaceShellHost
          services={{
            route,
            drawingFocusMode,
            navOpen,
            setNavOpen,
            onNavigate: (r: string) => navigate(r as Route),
            openSmartIngestion: () => useFloatingWindowStore.getState().openWindow('smart-ingestion'),
            toggleNightOps: () => useNightOpsStore.getState().toggle(),
            openCommandPalette: () => useUXControlStore.getState().openCommandPalette(),
          }}
          slots={{
            mobileToggle: !drawingFocusMode ? (
              <button
                type="button"
                data-testid="mobile-nav-toggle"
                onClick={() => setNavOpen((v) => !v)}
                className="md:hidden fixed top-3 left-3 z-[60] h-10 w-10 grid place-items-center rounded-lg"
                style={{ background: 'rgba(8,16,36,0.92)', border: '1px solid rgba(37,181,168,0.3)', color: '#5eead4' }}
                aria-label="Toggle navigation"
              >
                {navOpen ? <CloseIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
              </button>
            ) : null,
            mobileScrim: navOpen && !drawingFocusMode ? (
              <div data-testid="mobile-nav-scrim" className="md:hidden fixed inset-0 z-[40]" style={{ background: 'rgba(2,6,12,0.6)' }} onClick={() => setNavOpen(false)} />
            ) : null,
            sidebar: <Sidebar route={route} onNavigate={(r: string) => navigate(r as Route)} />,
            healthBanner: !drawingFocusMode ? <SystemHealthBanner /> : null,
            topBar: !drawingFocusMode ? <TopBar route={route} onNavigate={(r: string) => navigate(r as Route)} /> : null,
            upsell: !drawingFocusMode ? <WhatYoureMissing route={route} onNavigate={(r: string) => navigate(r as Route)} /> : null,
            routeContent: (
          <main className={`flex-1 overflow-y-auto ${drawingFocusMode ? 'pb-0' : 'pb-7'}`} style={{ background: 'rgba(6, 12, 26, 0.65)' }}>
            <ErrorBoundary>
              <Suspense fallback={<PageFallback />}>
                {route === 'dashboard' && <DashboardPage onNavigate={(r: string) => navigate(r as Route)} />}
                {route === 'rack-maker' && <RackDrafter onClose={() => navigate('dashboard')} />}
                {route === 'rack-maker-activate' && <LoginPage />}
                {route === 'assets'    && <AssetsPage />}
                {route === 'buildings' && <BuildingsPage />}
                {route === 'racks'     && <RacksPage />}
                {route === 'projects'  && <ProjectsPage />}
                {route === 'clashes'   && <ClashDetectionPage />}
                {route === 'documents' && <DocumentsPage />}
                {route === 'schedule' && <SchedulePage />}
                {route === 'sync'      && <SyncPage />}
                {route === 'ai'        && <AIAssistantPage />}
                {route === 'settings'  && <SettingsPage onNavigate={(r: string) => navigate(r as Route)} />}
                {route === 'cabling'   && <CablingPage />}
                {route === 'coach-validation' && <CoachValidationPage />}
                {route === 'comms'            && <CommsPage />}
                {route === 'autopilot'        && <AutopilotPage />}
                {route === 'agent'            && <AgentProjectsPage />}
                {route === 'cost'             && <CostTemplatesPage />}
                {route === 'backup'           && <BackupPage />}
                {route === 'health'           && <HealthPage />}
                {route === 'field'            && <FieldPage />}
                {route === 'propagation'      && <PropagationPage />}
                {route === 'doc-control'      && <DocumentControlPanel />}
                {route === 'creator'          && <CreatorPage />}
                {route === 'sie'             && <SmartIngestionPage />}
                {route === 'auto-report'    && <AutoReportPage />}
                {route === 'pricing'        && <PricingPage onVerify={verifyLicenseKey} onNavigate={(r) => navigate(r as Route)} />}
                {route === 'global-command' && <GlobalCommandPage />}
                {route === 'help'          && <SupportPage />}
                {route === 'parts-library' && <PartsLibraryPage />}
                {route === 'monitor'       && <MonitorDashboardPage />}
                {route === 'critical-infra' && <CriticalInfraPage />}
                {route === 'intelligence'  && <IntelligencePage />}
                {route === 'estimator'     && <EstimatorPage />}
                {route === 'civil'         && <CivilEstimatorPage />}
                {route === 'labor-timeline' && <LaborTimelinePage />}
                {route === 'certification-pass' && <CertificationPassPage />}
                {route === 'capabilities'  && <CapabilitiesPage onNavigate={(r: string) => navigate(r as Route)} />}
                {route === 'r2-brain'      && <R2BrainPage onNavigate={(r: string) => navigate(r as Route)} />}
                {route === 'r2-academy'    && <R2AcademyPage />}
                {route === 'r2-news'       && <R2NewsPage />}
                {route === 'r2-voice'      && <R2VoicePage />}
                {route === 'mie'           && <MIEPage />}
                {route === 'rail-engine'   && <R2RailEnginePage />}
                {route === 'founder-approvals' && <FounderApprovalsPage />}
                {route === 'quote-generator' && <QuoteGeneratorPage />}
                {route === 'marine-cable'  && <R2MarineCablePage />}
                {route === 'community-builder' && <R2CommunityBuilderPage />}
                {route === 'venue-studio' && <R2VenueStudioPage />}
                {route === 'manufacturing' && <R2ManufacturingPage />}
                {route === 'frequency-lab' && <R2FrequencyLabPage />}
                {route === 'integration-bridge' && <R2IntegrationBridgePage />}
                {route === 'bunker-builder' && <R2BunkerBuilderPage />}
                {route === 'site-audit' && <R2SiteAuditPage />}
                {route === 'markup-checkout' && <R2MarkupCheckoutPage />}
                {route === 'vending-machine' && <EngineeringVendingMachinePage onNavigate={(r: string) => navigate(r as Route)} />}
                {route === 'hospital-generator' && <R2HospitalGeneratorPage />}
                {route === 'viie' && <R2VerticalInfraPage />}
                {route === 'reality-translator' && <R2RealityTranslatorPage />}
                {route === 'finance' && <R2FinancePage />}
                {route === 'finance-pro' && <R2FinanceProPage />}
                {route === 'underground-corridor' && <R2UndergroundCorridorPage />}
                {route === 'carbon-intelligence' && <R2CarbonIntelligencePage />}
                {route === 'business-creator' && <R2BusinessCreatorPage />}
                {route === 'marketing-controls' && <R2MarketingControlsPage />}
                {route === 'managed-services' && <R2ManagedServicePage />}
                {route === 'bim-project-config'    && <BIMProjectConfigPage />}
                {route === 'bim-operator-dashboard' && <BIMOperatorDashboard />}
                {route === 'bim-engine'             && <BIMGenerationEnginePage />}
                {route === 'marketplace'            && <MarketplacePage />}
                {route === 'marketplace-orders'     && <MarketplaceOrdersPage />}
                {route === 'marketplace-operator'   && <MarketplaceOperatorPage />}
                {route === 'investor-relations'     && <InvestorRelationsPage />}
                {route === 'index'                  && <RCDIndexPage onNavigate={(r: string) => navigate(r as Route)} />}
                {route === 'job-queue'              && <JobQueuePage />}
                {route === 'feature-flags'          && <FeatureFlagManagerPage />}
                {route === 'global-map'             && <StandaloneMapPage />}
                {/* Route aliases — kept for index directory links */}
                {route === 'voice'                  && <R2VoicePage />}
                {route === 'r2-finance'             && <R2FinancePage />}
                {route === 'profile'                && <SettingsPage onNavigate={(r: string) => navigate(r as Route)} />}
                {/* Creative Intelligence Engine */}
                {route === 'cie'                    && <CIEPage />}
                {/* Automation Studio */}
                {route === 'automation-studio'      && <AutomationStudioPage />}
                {/* R2 Intelligent Project Calendar */}
                {route === 'r2-calendar'            && <R2CalendarPage />}
                {/* R2 Safety Intelligence */}
                {route === 'r2-safety'              && <R2SafetyPage />}
                {/* R2 Financial Command Centre */}
                {route === 'r2-financial-command'   && <R2FinancialCommandPage />}
                {/* R2 Timesheet Automation */}
                {route === 'r2-timesheet'           && <R2TimesheetPage />}
                {/* R2 Personal Intelligence Engine */}
                {route === 'r2-pie'                 && <R2PIEPage />}
                {/* R2 Finance Hub */}
                {route === 'r2-finance-hub'          && <R2FinanceHubPage />}
                {/* R2 Mission Control */}
                {route === 'r2-mission-control'      && <R2MissionControlPage />}
                {/* R2 AI Coach */}
                {route === 'r2-ai-coach'             && <R2AICoachPage />}
                {/* R2 Permit Intelligence Engine */}
                {route === 'r2-permit-intelligence'  && <R2PermitIntelligencePage />}
                {/* Founder Mode: R2 Core Ecosystem Map */}
                {route === 'r2-core-map'             && <R2CoreMapPage />}
                {/* R2 8K Studio */}
                {route === 'r2-studio'               && <R2StudioPage />}
                {/* R2 3D Character Creator */}
                {route === 'r2-character-creator'    && <R2CharacterCreatorPage />}
                {/* R2 Virtual Production Studio */}
                {route === 'r2-production-studio'    && <R2ProductionStudioPage />}
                {/* R2 Calendar Engine */}
                {route === 'r2-calendar-engine'      && <R2CalendarEnginePage />}
                {/* R2 Voice Experience Engine */}
                {route === 'r2-voice-engine'         && <R2VoiceEnginePage />}
                {/* R2 Role Agent Engine */}
                {route === 'r2-role-agent'           && <R2RoleAgentPage />}
                {/* R2 Cinematic Scene Generator */}
                {route === 'r2-cinematic-scene'      && <R2CinematicScenePage />}
                {/* R2 CSIE — Cinematic Scene Intelligence Engine */}
                {route === 'r2-csie'                 && <R2CSIEPage />}
                {/* R2 Creative Studio — Platform Hub */}
                {route === 'r2-creative-studio'      && <R2CreativeStudioPage onNavigate={(r: string) => navigate(r as Route)} />}
                {/* R2 SCSE — Strategy & Cinematic Simulation Engine */}
                {route === 'r2-scse'                 && <R2SCSEPage />}
                {/* R2 ECE — Epic Cinema Engine */}
                {route === 'r2-ece'                  && <R2ECEPage />}
                {/* R2 PCE — Performance & Choreography Engine */}
                {route === 'r2-pce'                  && <R2PCEPage />}
                {/* R2 VCE — Virtual Cinematography Engine */}
                {route === 'r2-vce'                  && <R2VCEPage />}
                {/* R2 WBE — World Builder Engine */}
                {route === 'r2-wbe'                  && <R2WBEPage />}
                {/* R2 AIE — Asset Intelligence Engine */}
                {route === 'r2-aie'                  && <R2AIEPage />}
                {/* R2 GLE — Global Localization Engine */}
                {route === 'r2-gle'                  && <R2GLEPage />}
                {/* R2 MVIE — Music Video Intelligence Engine */}
                {route === 'r2-mvie'                 && <R2MVIEPage />}
                {/* R2 AIQE — Audio Intelligence & Quantization Engine */}
                {route === 'r2-aiqe'                 && <R2AIQEPage />}
                {/* R2 CTEE — Civilization & Time Evolution Engine */}
                {route === 'r2-ctee'                 && <R2CTEEPage />}
                {/* R2 REE — R2 Economy Engine */}
                {route === 'r2-ree'                  && <R2REEPage />}
                {/* R2 ARIE — Arena Intelligence Engine */}
                {route === 'r2-arie'                 && <R2ARIEPage />}
                {/* R2 VIE — Visual Intelligence Engine */}
                {route === 'r2-vie'                  && <R2VIEPage />}
                {/* R2 CCFE — Content Classification & Creative Freedom Engine */}
                {route === 'r2-ccfe'                 && <R2CCFEPage />}
                {/* R2 LIE — Life Intelligence Engine */}
                {route === 'r2-lie'                  && <R2LIEPage />}
                {/* R2 CATE — Catastrophe Engine */}
                {route === 'r2-cate'                 && <R2CATEPage />}
                {/* R2 Founder Command Centre */}
                {route === 'r2-founder-command-center' && <R2FounderCommandCenterPage />}
                {/* R2 BWSE — Building World Simulation Engine */}
                {route === 'r2-bwse'                  && <R2BSWEPage />}
                {/* R2 RAE — Revit Adaptive Engine */}
                {route === 'r2-rae'                   && <R2RAEPage />}
                {/* R2DL — R2 Design Language */}
                {route === 'r2-r2dl'                  && <R2DesignLanguagePage />}
                {/* R2 DCRE — Disaster & Resilience Engine */}
                {route === 'r2-dcre'                  && <R2DCREPage />}
                {route === 'r2-3d-engine'             && <R23DIntelligencePage />}
                {route === 'r2-smart-viewer'          && <R2SmartViewerPage />}
                {route === 'r2-isle'                  && <R2ISLEPage />}
                {route === 'r2-mpce'                  && <R2MPCEPage />}
                {route === 'r2-wiie'                  && <R2WIIEPage />}
                {route === 'r2-lvtde'                 && <R2LVTDEPage />}
                {route === 'r2-lice'                  && <R2LICEPage />}
                {route === 'r2-uiie'                  && <R2UIIEPage />}
                {route === 'r2-ctie'                  && <R2CTIEPage />}
                {route === 'r2-mfie'                  && <R2MFIEPage />}
                {route === 'r2-ctdte'                 && <R2CTDTEPage />}
                {route === 'r2-ase'                   && <R2ASEPage />}
                {route === 'r2-meeting-intelligence'  && <R2MeetingIntelligencePage />}
                {route === 'r2-permissions'           && <R2PermissionsPage />}
                {route === 'r2-arch-engine'           && <R2ArchEnginePage />}
                {route === 'r2-dc-profile'            && <R2DCProfilePage />}
                {route === 'r2-annotations'           && <R2AnnotationsPage />}
                {route === 'r2-wsie'                  && <R2WSIEPage />}
                {route === 'r2-measurement-engine'    && <R2MeasurementEnginePage />}
                {route === 'r2-iie'                   && <R2IIEPage />}
                {route === 'r2-ai-gateway'           && <R2AIGatewayPage />}
                {route === 'r2-memory-engine'        && <R2MemoryEnginePage />}
                {route === 'r2-globe'                  && <R2GlobeEnginePage />}
                {route === 'r2-infra-journey'         && <R2InfraJourneyPage />}
                {route === 'r2-digital-twin-library'  && <R2DigitalTwinLibraryPage onNavigateToBIMStudio={() => navigate('r2-bim-studio')} />}
                {route === 'r2-bim-studio'            && <R2BIMStudioPage />}
                {route === 'r2-global-ops-center'     && <R2GlobalOpsCenterPage />}
                {route === 'r2-old-earth-world'       && <R2OldEarthWorldPage />}
                {route === 'r2-marketplace'               && <R2MarketplacePage />}
                {/* R2 Customer Platform — Delivery, Dashboard, Vending Machine */}
                {route === 'r2-delivery-portal'           && <R2DeliveryPortalPage />}
                {route === 'r2-customer-dashboard'        && <R2CustomerDashboardPage onNavigate={navigate} />}
                {route === 'r2-vending-machine'           && <R2VendingMachineLanding />}
                {/* R2 AI Generator + GIS + Digital Twin */}
                {route === 'r2-drawing-generator'         && <R2AIDrawingGeneratorPage />}
                {route === 'r2-design-package-generator'  && <R2DesignPackageGeneratorPage />}
                {route === 'r2-gis-intelligence'          && <R2GISIntelligencePage />}
                {route === 'r2-intelligent-building'      && <R2IntelligentBuildingPage />}
                {route === 'r2-bins-explorer'             && <R2BINSPage />}
                {route === 'r2-epe'                       && <R2EPEPage />}
                {route === 'r2-sustainability'            && <R2SustainabilityPage />}
                {route === 'r2-equipment-library'         && <R2EquipmentLibraryPage />}
                {route === 'r2-certification'             && <R2CertificationPage />}
              </Suspense>
            </ErrorBoundary>
          </main>
            ),
            footer: <Footer />,
            pfeMount: <PfeMount />,
            ingestMount: <IngestMount />,
            launcher: <CyberpunkLauncher onNavigate={(r: string) => navigate(r as Route)} onOpenIngest={() => useFloatingWindowStore.getState().openWindow('smart-ingestion')} />,
            intercom: <CorporateIntercom />,
            avatarOverlay: <GlobalAvatarOverlay />,
            focusBeamExit: <FocusBeamExitChip />,
            windowManager: <WindowManagerProvider />,
          }}
        />
      </AppShell>
    </>
  );
}

function PfeMount(): JSX.Element {
  const pfe = usePfeStore();
  return (
    <ProjectFileExplorer
      projectId={pfe.projectId}
      open={pfe.open}
      onOpenChange={(v) => v ? useFloatingWindowStore.getState().openWindow('project-explorer') : useFloatingWindowStore.getState().closeWindow('project-explorer')}
    />
  );
}

function IngestMount(): JSX.Element {
  const ing = useIngestStore();
  return <QuickIngestDialog open={ing.open} onClose={() => useFloatingWindowStore.getState().closeWindow('smart-ingestion')} />;
}

